import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useHistory, Redirect } from "react-router-dom";
import { useSelector } from "react-redux";
import HSidebarPro from "../../hcomponents/HSidebarPro";
import HSidebarMobile from "../../hcomponents/HSidebarMobile";
import { getUser, getActiveSubscription, shouldSeeHostExperience, getRedirectPathForUser } from "../../services/localService";
import { RoleConstants } from "../../utils/constants";
import EmailVerificationBanner from "../EmailVerificationBanner";
// removed local mobile header; trigger now comes from HHeader via custom event

// Import host experience styles
import "../../screens/host-experience/host-experience.module.css";

// Host Experience Components
import HostDashboard from "../../screens/host-experience/host-dashboard";
import HostCustomerDashboard from "../../screens/host-experience/host-customer-dashboard";

// Host layout with customized sidebar and header for the host experience
const HostAuthenticatedLayout = ({ children }) => {
  const location = useLocation();
  const history = useHistory();
  const path = location.pathname;
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Get user details
  const user = getUser();
  const userRole = user?.organizationRole?.code || user?.role || "";
  const orgTypeCode = user?.organization?.type?.code || "";
  const isCustomer = RoleConstants.isRoleInGroup(userRole, "HOST_CUSTOMER_ROLES");

  // Get subscription status from Redux
  const subscriptionState = useSelector(state => state.subscription);
  const { activeSubscription, loading: subscriptionLoading } = subscriptionState;

  // Check localStorage for cached subscription as fallback while Redux loads
  const cachedSubscription = useMemo(() => getActiveSubscription(), []);
  const isDebugSkipSub =
    typeof window !== "undefined" &&
    window.location.hostname === "localhost" &&
    localStorage.getItem("debug_skip_subscription") === "true";
  const hasActiveSubscription = !!activeSubscription || !!cachedSubscription || isDebugSkipSub;

  // Check if user requires subscription
  // HOST_PROPERTY_MANAGEMENT and CORPORATE_CUSTOMER require subscription
  // ROLE_HOST_CUSTOMER_INDIVIDUAL does not require subscription even under these org types
  const requiresSubscription =
    (orgTypeCode === "HOST_PROPERTY_MANAGEMENT" || orgTypeCode === "CORPORATE_CUSTOMER") &&
    userRole !== "ROLE_HOST_CUSTOMER_INDIVIDUAL";

  // Hide sidebar if user requires subscription but doesn't have one
  const shouldShowSidebar = !requiresSubscription || hasActiveSubscription;

  // Redirect to pricing if subscription is required but not active
  useEffect(() => {
    // Don't redirect while subscription is loading
    if (subscriptionLoading) {
      return;
    }

    if (requiresSubscription && !hasActiveSubscription) {
      const currentPath = location.pathname;
      const pricingPath = orgTypeCode === "CORPORATE_CUSTOMER" ? "/subscriber/pricing" : "/host/pricing";
      const subscriptionUpgradePath = "/host/subscription-upgrade";

      // Only redirect if not already on pricing page or subscription upgrade page
      if (currentPath !== pricingPath && !currentPath.startsWith(subscriptionUpgradePath)) {
        history.push(pricingPath);
      }
    }
  }, [requiresSubscription, hasActiveSubscription, subscriptionLoading, orgTypeCode, location.pathname, history]);

  // Listen for global header trigger to open mobile sidebar
  useEffect(() => {
    const handleOpen = () => setMobileSidebarOpen(true);
    const handleClose = () => setMobileSidebarOpen(false);
    window.addEventListener("host:openMobileSidebar", handleOpen);
    window.addEventListener("host:closeMobileSidebar", handleClose);
    return () => {
      window.removeEventListener("host:openMobileSidebar", handleOpen);
      window.removeEventListener("host:closeMobileSidebar", handleClose);
    };
  }, []);

  // Check if user is authenticated
  if (!user) {
    return <Redirect to="/signin" />;
  }

  // Enforce boundary: Non-host users (individual & corporate consumers) cannot access host layout
  if (!shouldSeeHostExperience()) {
    const redirectPath = getRedirectPathForUser() || "/startbooking";
    return <Redirect to={redirectPath} />;
  }

  // Enforce boundary between Host PM and Tenant Customer
  if (isCustomer) {
    const hostAdminRoutes = ["/host/dashboard", "/host/spaces", "/host/invoice", "/host/analytics", "/host/pricing", "/host/billing-management"];
    if (hostAdminRoutes.some(r => path.startsWith(r))) {
      return <Redirect to="/host/customer-dashboard" />;
    }
  } else if (path === "/host/customer-dashboard") {
    return <Redirect to="/host/dashboard" />;
  }



  return (
    <div
      className="flex bg-[#F1F4FA] h-screen overflow-hidden hostExperienceContainer"
      style={{ overscrollBehavior: "none" }}
    >
      {/* Desktop Sidebar - Hidden on mobile */}
      {shouldShowSidebar && (
        <div className="hidden lg:block">
          <HSidebarPro onExpandChange={expanded => setSidebarExpanded(expanded)} />
        </div>
      )}

      {/* Mobile Sidebar - Only visible on mobile */}
      {shouldShowSidebar && (
        <div className="lg:hidden">
          <HSidebarMobile isOpen={mobileSidebarOpen} onClose={() => setMobileSidebarOpen(false)} />
        </div>
      )}

      {/* Main Content Area */}
      <div
        className={`flex-1 flex flex-col h-full overflow-hidden ${
          shouldShowSidebar ? (sidebarExpanded ? "lg:pl-60" : "lg:pl-16") : ""
        } transition-all duration-300`}
      >
        {/* Mobile header removed; HHeader triggers mobile sidebar */}

        {/* Main Content Area - Structured for fixed header */}
        <div className="flex overflow-hidden flex-col h-full">
          <EmailVerificationBanner user={user} />
          <div className="overflow-y-auto flex-1 min-h-0 bg-[#F1F4FA] w-full">
            {path === "/host" || path === "/host/customer-dashboard" ? (
              isCustomer ? (
                <HostCustomerDashboard />
              ) : (
                <HostDashboard />
              )
            ) : (
              children
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HostAuthenticatedLayout;
